import {useCallback, useState} from 'react'
import {ErrorText} from '@/components/shared/ErrorText'
import type {ReactNode} from 'react'
import {Fingerprint, KeyRound, Plus, Tag} from 'lucide-react'
import {useI18n} from '@/i18n'
import {Stack} from '@/components/shared/Stack'
import {DatePicker} from '@/components/shared/DatePicker'
import {AppLocalDate} from '@/lib/date-utils'
import {Label} from '@/components/ui/label'
import {Button} from '@/components/ui/button'
import {Card, CardContent, CardHeader, CardTitle} from '@/components/ui/card'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'
import {useAppConfig, labelOf} from '@/hooks/use-app-config'
import {IdentifierRow} from '../components/IdentifierRow'
import {EntityLayoutRenderer, useEntityFieldOverrides} from '@/components/form-layout/renderer/EntityLayoutRenderer'
import {AttributeLayoutRenderer} from '@/components/form-layout/renderer/AttributeLayoutRenderer'
import {TEMPLATES} from '../types'
import type {OnboardingFormState, IdentifierFormRow} from '../types'
import {formatDateOnly} from '@/lib/date-utils'
import {IdentityLifecycleStateBadge} from '@/types/identity'
import type {IdentityLifecycleState} from '@/types/identity'
import {OnboardingRequestFields as F} from '@/types/onboarding'
import {InlineRow} from '@/components/shared/InlineRow'
import {Icon} from '@/components/shared/Icon'
import {MutedText} from '@/components/shared/MutedText'

interface IdentityStepProps {
    form: OnboardingFormState
    onUpdate: <K extends keyof OnboardingFormState>(field: K, value: OnboardingFormState[K]) => void
    onSetIdentifiers: (identifiers: IdentifierFormRow[]) => void
    onSetIdentityAttributes: (attrs: OnboardingFormState['identityAttributes']) => void
    onIdentifierDuplicateChange: (hasDuplicate: boolean) => void
}

let identifierCounter = 1

/**
 * Step 3: Identity — type, dates, identifiers, identity attributes.
 */
export function IdentityStep({
    form,
    onUpdate,
    onSetIdentifiers,
    onSetIdentityAttributes,
    onIdentifierDuplicateChange,
}: IdentityStepProps) {
    const i18n = useI18n()
    const {identityTypes, identifierTypes, lifecycleStates} = useAppConfig()
    const template = TEMPLATES.find(t => t.key === form.templateKey)
    /* eslint-disable-next-line i18next/no-literal-string */
    const fieldOverrides = useEntityFieldOverrides('identity-entity')
    const [duplicateErrors, setDuplicateErrors] = useState<Set<string>>(new Set())

    const handleIdentifierChange = useCallback((index: number, updated: IdentifierFormRow) => {
        const newIdentifiers = [...form.identifiers]
        newIdentifiers[index] = updated
        onSetIdentifiers(newIdentifiers)
    }, [form.identifiers, onSetIdentifiers])

    // Identifier types already in use
    const usedTypes = new Set(form.identifiers.map(r => r.identifierType))
    // eslint-disable-next-line i18next/no-literal-string -- typed identifier type constants
    const allTypes = ['EMAIL', 'UPN', 'EMPLOYEE_NO', 'CUSTOM'] as const
    const availableTypes = allTypes.filter(t => !usedTypes.has(t))

    const handleAddIdentifier = useCallback(() => {
        if (availableTypes.length === 0) return
        identifierCounter++
        const newRow: IdentifierFormRow = {
            id: `identifier-${identifierCounter}`, // eslint-disable-line i18next/no-literal-string
            identifierType: availableTypes[0],
            identifierValue: '',
            primary: false,
            fixed: false,
        }
        onSetIdentifiers([...form.identifiers, newRow])
    }, [form.identifiers, onSetIdentifiers, availableTypes])

    const handleRemoveIdentifier = useCallback((index: number) => {
        const row = form.identifiers[index]
        if (row.fixed) return // Cannot remove email row
        const newIdentifiers = form.identifiers.filter((_, i) => i !== index)
        // If removed row was primary, make email primary
        if (row.primary && newIdentifiers.length > 0) {
            newIdentifiers[0] = {...newIdentifiers[0], primary: true}
        }
        onSetIdentifiers(newIdentifiers)
        // Clean up duplicate tracking
        const newErrors = new Set(duplicateErrors)
        newErrors.delete(row.id)
        setDuplicateErrors(newErrors)
        onIdentifierDuplicateChange(newErrors.size > 0)
    }, [form.identifiers, onSetIdentifiers, duplicateErrors, onIdentifierDuplicateChange])

    const handlePrimaryChange = useCallback((index: number) => {
        const newIdentifiers = form.identifiers.map((r, i) => ({
            ...r,
            primary: i === index,
        }))
        onSetIdentifiers(newIdentifiers)
    }, [form.identifiers, onSetIdentifiers])

    const handleDuplicateStatus = useCallback((rowId: string, hasError: boolean) => {
        setDuplicateErrors(prev => {
            const next = new Set(prev)
            if (hasError) next.add(rowId)
            else next.delete(rowId)
            onIdentifierDuplicateChange(next.size > 0)
            return next
        })
    }, [onIdentifierDuplicateChange])

    // Date validation
    const today = AppLocalDate.today()
    const isEntryDatePast = form.entryDate && form.entryDate < today
    const isExitBeforeEntry = form.exitDate && form.entryDate && form.exitDate < form.entryDate

    return (
        <Stack gap="lg">
            {/* Existing identities card (existing party flow) */}
            {form.existingPartyData?.identities && form.existingPartyData.identities.length > 0 && (
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-sm" data-testid="card-title-existing-identities">{i18n.onboarding.identity.existingIdentities}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <Stack gap="sm">
                            {form.existingPartyData.identities.map(ident => (
                                <InlineRow key={ident.id} gap="sm">
                                    {(() => {
                                        const lifecycleBadge = IdentityLifecycleStateBadge.of(ident.lifecycleState as IdentityLifecycleState)
                                        const StateIcon = lifecycleBadge?.icon
                                        return (
                                            <Badge variant={lifecycleBadge?.variant ?? BadgeVariant.OUTLINE} className="gap-1">
                                                {StateIcon && <StateIcon className="h-3 w-3" aria-hidden="true"/>}
                                                {labelOf(lifecycleStates, ident.lifecycleState)}
                                            </Badge>
                                        )
                                    })()}
                                    <span>{labelOf(identityTypes, ident.identityType)}</span>
                                    {ident.entryDate && <MutedText>{formatDateOnly(ident.entryDate)}</MutedText>}
                                </InlineRow>
                            ))}
                        </Stack>
                    </CardContent>
                </Card>
            )}

            {/* Existing identifiers card (existing party flow) */}
            {form.existingPartyData?.identifiers && form.existingPartyData.identifiers.length > 0 && (
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-sm" data-testid="card-title-existing-identifiers">{i18n.onboarding.identity.existingIdentifiers}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <Stack gap="sm">
                            {form.existingPartyData.identifiers.map(ident => (
                                <InlineRow key={ident.id} gap="sm">
                                    <Badge variant="outline">{labelOf(identifierTypes, ident.identifierType)}</Badge>
                                    <span>{ident.identifierValue}</span>
                                    {ident.primary && <Badge variant="secondary">{i18n.onboarding.identity.primary}</Badge>}
                                </InlineRow>
                            ))}
                        </Stack>
                    </CardContent>
                </Card>
            )}

            {/* Identity Details */}
            <Card>
                <CardHeader>
                    <CardTitle data-testid="card-title-identity-details">
                        <Icon icon={Fingerprint} size="lg"/>
                        {i18n.onboarding.identity.title}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {/* Identity entity fields — layout-driven via EntityLayoutRenderer */}
                    {(() => {
                        const entityFields: Record<string, ReactNode> = {}
                        const itOverrides = fieldOverrides[F.identityType]
                        const edOverrides = fieldOverrides[F.entryDate]
                        const xdOverrides = fieldOverrides[F.exitDate]

                        // Identity Type (visible for Custom template only)
                        if (template?.showIdentityType) {
                            entityFields[F.identityType] = (
                                <Stack className="space-y-1.5">
                                    <Label>{itOverrides?.label ?? i18n.onboarding.identity.identityType}</Label>
                                    <Select
                                        value={form.identityType}
                                        onValueChange={val => onUpdate(F.identityType, val)}
                                    >
                                        <SelectTrigger data-testid="select-identity-type">
                                            <SelectValue placeholder={i18n.onboarding.identity.identityTypePlaceholder}/>
                                        </SelectTrigger>
                                        <SelectContent>
                                            {identityTypes.map(t => (
                                                <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </Stack>
                            )
                        }

                        entityFields[F.entryDate] = (
                            <Stack className="space-y-1.5">
                                <Label htmlFor="entry-date">{edOverrides?.label ?? i18n.onboarding.identity.entryDate}</Label>
                                <DatePicker
                                    testId="input-entry-date"
                                    value={form.entryDate}
                                    onChange={(iso) => onUpdate(F.entryDate, iso)}
                                    ariaLabel={String(edOverrides?.label ?? i18n.onboarding.identity.entryDate)}
                                />
                                {isEntryDatePast
                                    ? <ErrorText className="text-xs">{i18n.onboarding.identity.entryDatePastError}</ErrorText>
                                    : <MutedText size="xs">{i18n.onboarding.identity.entryDateHelper}</MutedText>
                                }
                            </Stack>
                        )

                        // Exit Date (Contractor template only)
                        if (template?.showExitDate) {
                            entityFields[F.exitDate] = (
                                <Stack className="space-y-1.5">
                                    <Label htmlFor="exit-date">{xdOverrides?.label ?? i18n.onboarding.identity.exitDate}</Label>
                                    <DatePicker
                                        testId="input-exit-date"
                                        value={form.exitDate}
                                        onChange={(iso) => onUpdate(F.exitDate, iso)}
                                        ariaLabel={String(xdOverrides?.label ?? i18n.onboarding.identity.exitDate)}
                                    />
                                    {isExitBeforeEntry
                                        ? <ErrorText className="text-xs">{i18n.onboarding.validation.exitBeforeEntry}</ErrorText>
                                        : <MutedText size="xs">{i18n.onboarding.identity.exitDateHelper}</MutedText>
                                    }
                                </Stack>
                            )
                        }

                        return (
                            <EntityLayoutRenderer
                                /* eslint-disable-next-line i18next/no-literal-string */
                                useCaseKey="identity-entity"
                                entityFields={entityFields}
                            />
                        )
                    })()}
                </CardContent>
            </Card>

            {/* Identifiers Section */}
            <Card>
                <CardHeader>
                    <CardTitle data-testid="card-title-identifiers">
                        <Icon icon={KeyRound} size="lg"/>
                        {i18n.onboarding.identity.identifiers}
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                    {form.identifiers.map((row, index) => (
                        <IdentifierRow
                            key={row.id}
                            row={row}
                            usedTypes={usedTypes}
                            onChange={(updated) => handleIdentifierChange(index, updated)}
                            onRemove={() => handleRemoveIdentifier(index)}
                            onPrimaryChange={() => handlePrimaryChange(index)}
                            onDuplicateStatusChange={(hasError) => handleDuplicateStatus(row.id, hasError)}
                        />
                    ))}

                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleAddIdentifier}
                        disabled={availableTypes.length === 0}
                        data-testid="btn-add-identifier"
                    >
                        <Icon icon={Plus} size="xs" className="mr-1"/>
                        {i18n.onboarding.identity.addIdentifier}
                    </Button>
                </CardContent>
            </Card>

            {/* Identity Attributes */}
            <Card>
                <CardHeader>
                    <CardTitle data-testid="card-title-identity-attributes">
                        <Icon icon={Tag} size="lg"/>
                        {i18n.onboarding.identity.additionalDetails}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <AttributeLayoutRenderer
                        /* eslint-disable-next-line i18next/no-literal-string */
                        useCaseKey="identity-attribute"
                         
                        entityType="IDENTITY"
                        typeCode={form.identityType}
                        values={form.identityAttributes}
                        onChange={onSetIdentityAttributes}
                    />
                </CardContent>
            </Card>
        </Stack>
    )
}
